import React from "react";
import { motion } from "framer-motion";
import {
  Sunrise,
  Coffee,
  GlassWater,
  UtensilsCrossed,
  Cookie,
  Soup,
  Moon,
} from "lucide-react";

const meals = [
  {
    icon: Sunrise,
    title: "Early Morning",
    detail: "A glass of warm milk to begin the day gently.",
  },
  {
    icon: Coffee,
    title: "Breakfast",
    detail:
      "Kerala classics, a different one each day: dosa, idli, upma, idiyappam, appam or puttu, made with steamed rice, wheat, multi-millet or ragi, served with tea or coffee.",
  },
  {
    icon: GlassWater,
    title: "11 AM · Mid-Morning",
    detail:
      "A nourishing mid-meal, such as a health mix shake, to keep strength steady.",
  },
  {
    icon: UtensilsCrossed,
    title: "1 PM · Lunch",
    detail:
      "A full Kerala meal: rice with curry (vegetarian or non-vegetarian, your choice), upperi (stir-fried vegetable), pickle and pappad.",
  },
  {
    icon: Cookie,
    title: "4 PM · Evening",
    detail: "Tea or coffee with a light homemade snack.",
  },
  {
    icon: Soup,
    title: "Dinner",
    detail:
      "Chapathi, appam, or rice dishes like pulav or fried rice, with vegetarian or non-vegetarian curries.",
  },
  {
    icon: Moon,
    title: "Bedtime",
    detail: "Warm milk, a boiled egg, and seasonal fruits before sleep.",
  },
];

export const Nourishment = () => {
  return (
    <section
      id="nourishment"
      className="relative py-16 md:py-36 bg-sand/40 bg-grain"
      data-testid="nourishment-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
            Nourishment
          </div>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">
            Seven meals, <span className="italic text-sage-700">one gentle rhythm</span>.
          </h2>
          <p className="mt-5 text-base md:text-lg text-charcoal/65 leading-relaxed">
            Food is half the healing in Prasava Raksha. Every meal of this
            postnatal diet is warm, freshly cooked in our kitchen, and adjusted
            to your digestion, recovery stage, and the doctor's guidance. This
            is the rhythm of a typical day.
          </p>
        </motion.div>

        <div className="mt-10 lg:mt-16 max-w-3xl relative">
          {/* vertical connecting line */}
          <div
            className="absolute left-[27px] md:left-[31px] top-2 bottom-2 w-px bg-sage/25"
            aria-hidden
          />

          <ol className="space-y-7 md:space-y-12">
            {meals.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.li
                  key={m.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{
                    duration: 0.7,
                    delay: i * 0.06,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="relative pl-20 md:pl-24"
                  data-testid={`meal-slot-${i + 1}`}
                >
                  <div className="absolute left-0 top-0 w-14 h-14 md:w-[62px] md:h-[62px] rounded-full bg-ivory border border-sage/30 flex items-center justify-center text-sage">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="pt-1 md:pt-2 font-serif text-2xl md:text-3xl text-charcoal">
                    {m.title}
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-charcoal/70 leading-relaxed max-w-xl">
                    {m.detail}
                  </p>
                </motion.li>
              );
            })}
          </ol>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 md:mt-14 pl-20 md:pl-24 text-xs md:text-sm text-charcoal/55 leading-relaxed italic"
            data-testid="nourishment-bystander-note"
          >
            Your bystander is served breakfast, lunch, evening tea and dinner
            alongside you, at no extra charge, no separate menu fuss.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default Nourishment;
